import { SsrType } from '@models/common/shared-enums.model';
import type {
  LoungeFlightTabItem,
  SpecialServiceResponseMapped,
} from '@models/landing/api/special-service.model';
import type { CodeTypeList } from '@models/master-data/master-data.model';

const DEFAULT_DECIMAL_PLACES = 2;
const LOUNGE_SSR_TYPE = String(SsrType.LOUNGE);

export type LoungeTabUnavailableReason = 'interline' | 'cut-off' | 'no-lounge';

export const resolveCurrencyDecimalPlaces = (currencyDecimalPlaces?: number): number => {
  if (
    typeof currencyDecimalPlaces === 'number' &&
    Number.isFinite(currencyDecimalPlaces) &&
    currencyDecimalPlaces >= 0
  ) {
    return Math.trunc(currencyDecimalPlaces);
  }

  return DEFAULT_DECIMAL_PLACES;
};

export const resolveLoungeTabValue = (
  logicalFlightId: string,
  physicalFlightId: string,
): string => {
  return physicalFlightId || logicalFlightId;
};

export const isMatchingLoungeTab = (
  activeFlightTab: string,
  logicalFlightId: string,
  physicalFlightId: string,
): boolean => {
  if (!activeFlightTab) {
    return true;
  }

  return activeFlightTab === physicalFlightId || activeFlightTab === logicalFlightId;
};

export const parseLoungeAmount = (amount: string): number => {
  const parsedAmount = Number(amount);
  return Number.isFinite(parsedAmount) ? parsedAmount : 0;
};

export const resolveLoungeCodeItem = (
  codeType: string,
  codeTypeList?: CodeTypeList,
): CodeTypeList[number] | undefined => {
  if (!codeTypeList || !codeType) {
    return undefined;
  }

  return codeTypeList.find((item) => item.codeID === codeType);
};

export const resolveUniqueLoungeQuotes = (
  specialService?: SpecialServiceResponseMapped,
): SpecialServiceResponseMapped['LoungeQuotes'] => {
  return (specialService?.LoungeQuotes ?? []).filter(
    (quote, index, quotes) =>
      quotes.findIndex(
        (currentQuote) =>
          currentQuote.logicalFlightId === quote.logicalFlightId &&
          currentQuote.physicalFlightId === quote.physicalFlightId,
      ) === index,
  );
};

const parseSelectedPassengerResPaxId = (selectedPassengerId?: string): number | null => {
  const parsedPassengerId = Number.parseInt(String(selectedPassengerId ?? ''), 10);

  return Number.isFinite(parsedPassengerId) ? parsedPassengerId : null;
};

export const resolveApplicableLoungeInfo = (
  loungeInfo: SpecialServiceResponseMapped['LoungeQuotes'][number]['LoungeInfo'],
  selectedPassengerId?: string,
): SpecialServiceResponseMapped['LoungeQuotes'][number]['LoungeInfo'] => {
  const selectedResPaxId = parseSelectedPassengerResPaxId(selectedPassengerId);

  if (selectedResPaxId === null) {
    return loungeInfo;
  }

  return loungeInfo.filter((info) => {
    const matchesApplicablePax = info.applicablePax.some(
      (applicablePax) => applicablePax.resPaxId === selectedResPaxId,
    );

    if (matchesApplicablePax) {
      return true;
    }

    return info.passengerDetails.some((passenger) => passenger.resPaxId === selectedResPaxId);
  });
};

export const resolvePassengerHasPurchasedLounge = (
  specialService: SpecialServiceResponseMapped | undefined,
  activeFlightTab: string,
  selectedPassengerId: string,
  codeType: string,
): boolean => {
  const selectedResPaxId = Number(selectedPassengerId);
  const selectedPassenger = specialService?.passengerList.find(
    (passenger) => passenger.resPaxId === selectedResPaxId,
  );

  if (!selectedPassenger) {
    return false;
  }

  return selectedPassenger.flightsDetail
    .filter((flightDetail) =>
      isMatchingLoungeTab(
        activeFlightTab,
        flightDetail.logicalFlightId,
        flightDetail.physicalFlightId,
      ),
    )
    .flatMap((flightDetail) => flightDetail.includedExtrasList)
    .some(
      (includedExtra) =>
        includedExtra.ssrType.toUpperCase() === LOUNGE_SSR_TYPE &&
        includedExtra.codeType === codeType &&
        (includedExtra.amount > 0 || includedExtra.isPurchased === true),
    );
};

export const resolveApplicableOrPurchasedLoungeInfo = (
  loungeInfo: SpecialServiceResponseMapped['LoungeQuotes'][number]['LoungeInfo'],
  specialService: SpecialServiceResponseMapped | undefined,
  activeFlightTab: string,
  selectedPassengerId?: string,
): SpecialServiceResponseMapped['LoungeQuotes'][number]['LoungeInfo'] => {
  const selectedResPaxId = parseSelectedPassengerResPaxId(selectedPassengerId);

  if (selectedResPaxId === null || !selectedPassengerId) {
    return loungeInfo;
  }

  return loungeInfo.filter((info) => {
    const matchesApplicablePax = info.applicablePax.some(
      (applicablePax) => applicablePax.resPaxId === selectedResPaxId,
    );

    if (matchesApplicablePax) {
      return true;
    }

    const matchesPassengerDetails = info.passengerDetails.some(
      (passenger) => passenger.resPaxId === selectedResPaxId,
    );

    if (matchesPassengerDetails) {
      return true;
    }

    return resolvePassengerHasPurchasedLounge(
      specialService,
      activeFlightTab,
      selectedPassengerId,
      info.codeType,
    );
  });
};

export const resolveLoungeInfoPurchased = (
  specialService: SpecialServiceResponseMapped | undefined,
  activeFlightTab: string,
  codeType: string,
): boolean => {
  if (!specialService) {
    return false;
  }

  return specialService.passengerList.some((passenger) =>
    passenger.flightsDetail.some(
      (flightDetail) =>
        isMatchingLoungeTab(
          activeFlightTab,
          flightDetail.logicalFlightId,
          flightDetail.physicalFlightId,
        ) &&
        flightDetail.includedExtrasList.some(
          (extra) =>
            extra.ssrType.toUpperCase() === LOUNGE_SSR_TYPE &&
            extra.codeType === codeType &&
            (extra.amount > 0 || extra.isPurchased),
        ),
    ),
  );
};

export const resolveLoungeTabUnavailableReason = (
  specialService: SpecialServiceResponseMapped | undefined,
  activeFlightTab: string,
  _flightTabItem: LoungeFlightTabItem | undefined,
): LoungeTabUnavailableReason | undefined => {
  const quote = resolveUniqueLoungeQuotes(specialService).find((currentQuote) =>
    isMatchingLoungeTab(
      activeFlightTab,
      currentQuote.logicalFlightId,
      currentQuote.physicalFlightId,
    ),
  );

  if (quote?.isWithinCutOff === true) {
    return 'cut-off';
  }

  return undefined;
};
